import React, { ChangeEvent, useEffect } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import isEmpty from 'lodash-es/isEmpty'
import { useForm } from 'react-hook-form'
import { TFunction, useTranslation } from 'react-i18next'
import {
  Avatar,
  Box,
  Button,
  Callout,
  Card,
  DataListItem,
  Input,
  Radio,
  Sheet,
  toast,
} from '@node-space/ui-components'
import { logSentryError } from '@node-space/utils'
import { RequestErrorCallout } from 'components/Errors/RequestErrorCallout'
import { useUpdatePaymentLimitMutation } from 'hooks/mutations/useUpdatePaymentLimitMutation'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { reactQueryName } from 'reactQueryKeys/reactQueryKeys'
import type { BaseErrorResponse } from 'types/beneficiaries'
import { LimitType } from 'types/paymentLimits'
import type { Limit, UpdateLimitPayload } from 'types/paymentLimits'
import { editLimitSchema, type EditLimitFormFields } from '../schemas/editLimitSchema'
import { formatPaymentMethodLabel } from '../utils'

interface EditLimitSheetProps {
  isOpen: boolean
  limit: Limit | null
  onClose: () => void
}

const getLimitTypeOptions = (t: TFunction<'translation', undefined>) => [
  { value: LimitType.TRANSACTION, label: t('paymentLimits.transactionLimit') },
  { value: LimitType.DAILY, label: t('paymentLimits.dailyLimit') },
]

export const EditLimitSheet = ({ isOpen, limit, onClose }: EditLimitSheetProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { mutate, isPending } = useUpdatePaymentLimitMutation()
  const { requestError, setRequestError, resetRequestError } = useMappedErrorMessage()

  const formSchema = editLimitSchema(t)

  const {
    register,
    setValue,
    watch,
    trigger,
    formState: { errors },
  } = useForm<EditLimitFormFields>({
    defaultValues: {
      limitType: LimitType.TRANSACTION,
      amount: '',
    },
    resolver: yupResolver(formSchema),
  })

  const formValues = watch()

  useEffect(() => {
    if (limit && isOpen) {
      setValue('limitType', limit?.limitType)
      setValue('amount', limit?.amount > 0 ? String(limit?.amount) : '')
      resetRequestError()
    }
  }, [limit, isOpen])

  const methodLabel = limit ? formatPaymentMethodLabel(limit?.qualifiers.method) : ''

  const handleSubmit = async () => {
    if (!limit) return

    const isValid = await trigger()
    if (!isValid) return

    resetRequestError()

    const payload: UpdateLimitPayload = {
      ruleId: limit.ruleId,
      method: limit.qualifiers.method,
      limitType: formValues.limitType as LimitType,
      amount: Number(formValues.amount),
    }

    mutate(payload, {
      onSuccess: () => {
        toast({ header: t('paymentLimits.limitUpdatedSuccess'), variant: 'success' })
        queryClient.invalidateQueries({
          queryKey: [reactQueryName.PAYMENT_LIMITS],
        })
        onClose()
      },
      onError: (error: BaseErrorResponse) => {
        setRequestError({ show: true, errorCode: error?.status })
        logSentryError('Error: useUpdatePaymentLimitMutation', error)
      },
    })
  }

  return (
    <Sheet
      isOpen={isOpen}
      header={t('paymentLimits.editSheetTitle', { method: methodLabel })}
      onClose={onClose}
    >
      <Box flex flexCol width="full" gap={24}>
        <Card variant="static" emphasis full>
          <Box flex justify="between">
            <DataListItem
              id="payment-method"
              layout="vertical"
              size="large"
              title={t('paymentLimits.paymentMethod')}
              body={methodLabel}
            />
            <DataListItem
              id="currency"
              layout="vertical"
              size="large"
              title={t('paymentLimits.currency')}
              BodyAvatar={<Avatar name={limit?.currency ?? ''} type="currency" size="small" />}
              body={limit?.currency ?? ''}
            />
          </Box>
        </Card>

        <Radio
          {...register('limitType')}
          label={t('paymentLimits.limitTypeLabel')}
          name="limitType"
          options={getLimitTypeOptions(t)}
          value={formValues?.limitType}
          onChange={value => setValue('limitType', value)}
          horizontal
          border
        />

        <Input
          {...register('amount')}
          label={t('paymentLimits.amountLabel')}
          type="text"
          inputMode="numeric"
          value={formValues?.amount}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setValue('amount', e?.target?.value, { shouldDirty: true, shouldValidate: true })
          }
          testId="edit-limit-amount"
          placeholderText="0.00"
          error={!!errors.amount}
          errorText={errors.amount?.message}
        />

        {requestError?.show && <RequestErrorCallout requestError={requestError} />}

        <Callout variant="info">{t('paymentLimits.editLimitInfoCallout')}</Callout>

        <Callout variant="warning">{t('paymentLimits.editLimitWarningCallout')}</Callout>

        <Box flex justify="between">
          <Button variant="secondary" onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button
            testId="change-limit-button"
            loading={isPending}
            disabled={!isEmpty(errors)}
            onClick={handleSubmit}
          >
            {t('paymentLimits.changeLimit')}
          </Button>
        </Box>
      </Box>
    </Sheet>
  )
}
